import React, { useEffect } from 'react';
import classNames from 'classnames';
import { EventBus } from 'src/common/EventBus';
import { Tabs } from 'src/common/form/tabs';
import OrganizationLink from 'src/common/OrganizationLink';
import { AddStrongIcon } from 'src/monet/icons';
import { PageSettingsProps } from 'src/pages/Settings';
import { functionNoop } from 'src/utils/function/noop';
import { FreeGatingTooltip } from 'src/common/free-gating/tooltip';
import { FreeGatingEnum, useFreeGatingStore } from 'src/common/free-gating/store';
import { useCurrentOrganization } from 'src/store/organization';
import { useCurrentSubscription } from 'src/store/subscription';
import { useFeatureRestrictions } from 'src/hooks/useFeatureRestrictions';

export const DashboardSettingsTabs = ({ activeTab }: PageSettingsProps) => {
  const onInviteMemberClicked = () => {
    EventBus.emit('dashboard-settings:show-invite-member-panel');
  };

  const setFreeGatingModalByType = useFreeGatingStore((state) => state.setFreeGatingModalByType);
  const freeGatingTooltipContent = useFreeGatingStore((state) => state.tooltipContent);
  const isFreeOrg = useCurrentOrganization((state) => state.isFreeOrg);
  const {isPro, isSubscribed, isEnterprise} = useCurrentSubscription(state => state);
  const { features } = useFeatureRestrictions();

  const onCreateWorkspaceClicked = () => {
    if (!features.canAccessWorkspaces) {
      setFreeGatingModalByType(FreeGatingEnum.WORKSPACE, true);
      return;
    }
    EventBus.emit('dashboard-settings:workspace-create');
  };

  useEffect(() => {
    if (activeTab === 'workspaces') {
      setFreeGatingModalByType(FreeGatingEnum.WORKSPACE, false);
    }
  }, [activeTab]);

  const workspaceContent = (
    <button
      type='button'
      className={classNames('flex btn-bedrock-secondary-small items-center ml-4', {
        'opacity-80': !features.canAccessWorkspaces,
      })}
      onClick={onCreateWorkspaceClicked}
    >
      <AddStrongIcon />
      <span className='ml-1'>Create workspace</span>
    </button>
  );

  return (
    <div className='flex items-center'>
      <Tabs
        theme='bedrock'
        value={activeTab}
        containerClassName={classNames('relative', {
          'after:block after:absolute after:w-px after:inset-y-0 after:-right-2 after:bg-bedrock-gray-medium':
            activeTab === 'members' || activeTab === 'workspaces' || activeTab === 'branding',
        })}
        onChange={functionNoop}
        options={[
          {
            value: 'organization',
            label: (
              <OrganizationLink to='/organization/settings' className='flex'>
                Organization
              </OrganizationLink>
            ),
          },
          {
            value: 'members',
            label: (
              <OrganizationLink to='/organization/members' className='flex journey-team-members'>
                Members
              </OrganizationLink>
            ),
          },
          {
            value: 'branding',
            label: (
              <OrganizationLink to='/branding' className='flex'>
                Brand
              </OrganizationLink>
            ),
          },
          {
            value: 'theme',
            label: (
              <OrganizationLink to='/theme' className='flex'>
                Theme
              </OrganizationLink>
            ),
          },
          {
            value: 'workspaces',
            label: (
              <OrganizationLink to='/workspaces' className='flex'>
                Workspaces
              </OrganizationLink>
            ),
          },
          {
            value: 'integrations',
            label: (
              <OrganizationLink to='/integrations' className='flex'>
                Integrations
              </OrganizationLink>
            ),
          },
          {
            value: 'billing',
            label: (
              <OrganizationLink to='/billing' className='flex'>
                Billing
              </OrganizationLink>
            ),
          },
          {
            value: 'account',
            label: (
              <OrganizationLink to='/account' className='flex'>
                Account
              </OrganizationLink>
            ),
          },
          {
            value: 'behavior',
            label: (
              <OrganizationLink to='/journey-behavior' className='flex'>
                Journey behavior
              </OrganizationLink>
            ),
          },
          {
            value: 'apikey',
            label: (
              <OrganizationLink to='/api-key-settings' className='flex'>
                API Keys
              </OrganizationLink>
            ),
          },
        ]}
      />
      {activeTab === 'members' && (
        <button
          type='button'
          className='flex btn-bedrock-secondary-small items-center ml-4'
          onClick={onInviteMemberClicked}
        >
          <AddStrongIcon />
          <span className='ml-1'>Invite member</span>
        </button>
      )}
      {activeTab === 'workspaces' && (
        <>
          {isFreeOrg ? (
            <FreeGatingTooltip placement='bottom' content={freeGatingTooltipContent}>
              {workspaceContent}
            </FreeGatingTooltip>
          ) : (
            workspaceContent
          )}
        </>
      )}
    </div>
  );
};
